import { formatDate } from '@betterment/core';

import { DateRangeOption } from '../../../graphql/schema';

export type CustomDateRange = { start: string; end: string };

const descriptionWithPrefix = (description: string) => (prefix: string) => `${prefix} ${description}`;

export const dateRangeOptions = [
  {
    label: 'All',
    value: DateRangeOption.AllTime,
    description: 'All time',
    prefixedDescription: descriptionWithPrefix('all time'),
  },
  {
    label: '5Y',
    value: DateRangeOption.PastFiveYears,
    description: 'Past 5 years',
    prefixedDescription: descriptionWithPrefix('past 5 years'),
  },
  {
    label: '3Y',
    value: DateRangeOption.PastThreeYears,
    description: 'Past 3 years',
    prefixedDescription: descriptionWithPrefix('past 3 years'),
  },
  {
    label: '1Y',
    value: DateRangeOption.PastYear,
    description: 'Past year',
    prefixedDescription: descriptionWithPrefix('past year'),
  },
  {
    label: 'YTD',
    value: DateRangeOption.YearToDate,
    description: 'Year to date',
    prefixedDescription: descriptionWithPrefix('year to date'),
  },
  {
    label: '3M',
    value: DateRangeOption.PastThreeMonths,
    description: 'Past 3 months',
    prefixedDescription: descriptionWithPrefix('past 3 months'),
  },
  {
    label: '1M',
    value: DateRangeOption.PastMonth,
    description: 'Past month',
    prefixedDescription: descriptionWithPrefix('past month'),
  },
  {
    label: 'Custom',
    value: DateRangeOption.Custom,
    description: 'Custom range',
    prefixedDescription: descriptionWithPrefix('(custom date range)'),
  },
];

export const isCustom = (value: DateRangeOption) => value === DateRangeOption.Custom;

export const isCustomDateRangeValid = (start: string, end: string) => {
  if (!start || !end) return false;

  const startDate = new Date(start);
  const endDate = new Date(end);

  if (startDate >= endDate) return false;

  // Earliest date supported by custody /v1/performance/search endpoint
  const minimumDate = new Date('2008-01-01');
  return !(startDate < minimumDate);
};

export const dateRangeFilterByValue = (value: DateRangeOption) =>
  dateRangeOptions.find(option => option.value === value);

export const dateRangeDescriptionByValue = (
  value: DateRangeOption,
  customDateRange?: { start: Date; end: Date },
  useLongCustomDescription?: boolean
): string => {
  const option = dateRangeFilterByValue(value);

  if (value === DateRangeOption.Custom && useLongCustomDescription && customDateRange) {
    return `${formatDate(customDateRange.start, 'longOrdinalShortMonth')} to ${formatDate(customDateRange.end, 'longOrdinalShortMonth')}`;
  }

  return option?.description || '';
};
